import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type BankAccountResponse,
  formatMoney,
  fromCents,
  LOAN_KIND_LABELS,
  LOAN_MOVEMENT_KIND_LABELS,
  type LoanResponse,
  toCents,
} from '@k-management/shared';
import { type ChangeEvent, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { type Command, useCommands, usePageTitle } from '../../components/layout/page-chrome.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { movementPath } from './treasury-ui.js';
import { LoanDialog, loanKeys } from './LoansPage.js';

/**
 * A loan (09/10/2026): its data, the debt left, every movement with capital,
 * interest and costs, the totals of each year and the plan of the bank.
 */
export function LoanPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canWrite = useCan('tesoreria.write');
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const loan = useQuery({
    queryKey: loanKeys.one(id),
    queryFn: () => apiRequest<LoanResponse>(`/treasury/loans/${id}`),
  });
  const banks = useQuery({
    queryKey: ['treasury', 'banks'],
    queryFn: () => apiRequest<BankAccountResponse[]>('/treasury/banks'),
  });
  usePageTitle(loan.data?.name ?? 'Mutuo');
  const remove = useMutation({
    mutationFn: () => apiRequest(`/treasury/loans/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['treasury'] });
      navigate('/tesoreria/mutui');
    },
  });
  const schedule = useMutation({
    mutationFn: (file: { base64: string; name: string }) =>
      apiRequest<LoanResponse>(`/treasury/loans/${id}/schedule`, {
        method: 'POST',
        body: { file: file.base64, fileName: file.name },
      }),
    onSuccess: (saved) => {
      queryClient.setQueryData(loanKeys.one(id), saved);
      void queryClient.invalidateQueries({ queryKey: loanKeys.all, exact: true });
    },
  });
  const choose = async (event: ChangeEvent<HTMLInputElement>) => {
    const chosen = event.target.files?.[0];
    event.target.value = '';
    if (!chosen) return;
    const bytes = new Uint8Array(await chosen.arrayBuffer());
    let binary = '';
    for (let index = 0; index < bytes.length; index += 8192) {
      binary += String.fromCharCode(...bytes.subarray(index, index + 8192));
    }
    schedule.mutate({ base64: btoa(binary), name: chosen.name });
  };
  useCommands(
    canWrite && loan.data
      ? [
          {
            key: 'edit',
            label: 'Modifica',
            icon: 'edit',
            onClick: () => setEditing(true),
          } satisfies Command,
        ]
      : [],
  );

  if (loan.isPending) return <p className="muted">Caricamento…</p>;
  if (loan.isError) return <p className="error">{loan.error.message}</p>;
  const data = loan.data;
  const bankName = (code: string) => banks.data?.find((bank) => bank.code === code)?.alias ?? code;
  const totalOf = (pick: (year: LoanResponse['years'][number]) => string) =>
    fromCents(data.years.reduce((sum, year) => sum + toCents(pick(year)), 0));

  return (
    <section className="stack">
      <div className="page-header">
        <div>
          <p className="muted small">
            <Link to="/tesoreria/mutui">← Mutui e finanziamenti</Link>
          </p>
          <h1>{data.name}</h1>
          <p className="muted">
            {[
              LOAN_KIND_LABELS[data.kind],
              data.lender,
              data.number && `n. ${data.number}`,
              `addebito su ${bankName(data.accountCode)}`,
              data.rate && `tasso ${data.rate}`,
              data.instalments && `${data.instalments} rate`,
              data.endDate && `ultima scadenza ${formatDate(data.endDate)}`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
        {!data.active && <span className="pill muted">disattivato</span>}
      </div>

      <div className="treasury-tiles">
        {data.amount && (
          <div className="treasury-tile">
            <span>Importo erogato</span>
            <strong>{formatMoney(data.amount)}</strong>
          </div>
        )}
        {data.residualAtStart && (
          <div className="treasury-tile">
            <span>Residuo al 31/12/2023</span>
            <strong>{formatMoney(data.residualAtStart)}</strong>
          </div>
        )}
        <div className="treasury-tile">
          <span>Debito residuo oggi</span>
          <strong>{data.residual === null ? '—' : formatMoney(data.residual)}</strong>
        </div>
        <div className="treasury-tile">
          <span>Capitale rimborsato (movimenti)</span>
          <strong>{formatMoney(totalOf((year) => year.capital))}</strong>
        </div>
        <div className="treasury-tile">
          <span>Interessi pagati (movimenti)</span>
          <strong>{formatMoney(totalOf((year) => year.interest))}</strong>
        </div>
        {data.nextInstalment && (
          <div className="treasury-tile">
            <span>Prossima rata {formatDate(data.nextInstalment.date)}</span>
            <strong>
              {data.nextInstalment.estimated && '~ '}
              {formatMoney(data.nextInstalment.amount)}
            </strong>
          </div>
        )}
      </div>
      {data.residual === null ? (
        <p className="notice small">
          Per il debito residuo serve un residuo noto a una data, oppure importo e data di
          erogazione: «Modifica».
        </p>
      ) : (
        data.anchorDate && (
          <p className="muted small">
            Il residuo parte da {formatMoney(data.anchorResidual!)} al {formatDate(data.anchorDate)}{' '}
            e segue la quota capitale di ogni rata.
          </p>
        )
      )}

      <h2>Movimenti</h2>
      {data.payments.length === 0 ? (
        <p className="muted">
          Nessun movimento trovato: le rate si riconoscono dal numero del mutuo nella descrizione
          della banca{data.number ? '' : ' (il numero non è indicato)'}; quelli senza numero si
          abbinano dalla riconciliazione con «Anticipo o finanziamento».
        </p>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Movimento</th>
                <th className="numeric">Importo</th>
                <th className="numeric">Capitale</th>
                <th className="numeric">Interessi</th>
                <th className="numeric">Spese e altro</th>
                <th className="numeric">Residuo dopo</th>
              </tr>
            </thead>
            <tbody>
              {data.payments.map((payment) => (
                <tr key={payment.movementId}>
                  <td>{formatDate(payment.date)}</td>
                  <td>
                    {LOAN_MOVEMENT_KIND_LABELS[payment.kind]}
                    {payment.instalment && ` n. ${payment.instalment}`}
                    <small className="muted block">
                      <Link to={movementPath(payment.accountCode, payment.movementId)}>
                        {bankName(payment.accountCode)}
                      </Link>
                      {payment.manual && ' · abbinato a mano'}
                      {payment.reversed && (
                        <span className="warn">
                          {' '}
                          · stornata il {formatDate(payment.reversed.on)}
                        </span>
                      )}
                    </small>
                  </td>
                  <td className="numeric">{formatMoney(payment.amount)}</td>
                  {payment.reversed ? (
                    <>
                      <td className="numeric muted">
                        <s>{formatMoney(payment.reversed.capital)}</s>
                      </td>
                      <td className="numeric muted">
                        <s>{formatMoney(payment.reversed.interest)}</s>
                      </td>
                      <td className="numeric muted">
                        <s>{formatMoney(payment.reversed.fees)}</s>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="numeric">{formatMoney(payment.capital)}</td>
                      <td className="numeric">{formatMoney(payment.interest)}</td>
                      <td className="numeric">{formatMoney(payment.fees)}</td>
                    </>
                  )}
                  <td className="numeric">
                    {payment.residualAfter === null ? '—' : formatMoney(payment.residualAfter)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data.years.length > 0 && (
        <>
          <h2>Per anno</h2>
          <p className="muted small">
            Interessi e spese sono oneri finanziari per il Controllo di gestione; il capitale non è
            un costo.
          </p>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Anno</th>
                  <th className="numeric">Capitale</th>
                  <th className="numeric">Interessi</th>
                  <th className="numeric">Spese e altro</th>
                </tr>
              </thead>
              <tbody>
                {data.years.map((year) => (
                  <tr key={year.year}>
                    <td>{year.year}</td>
                    <td className="numeric">{formatMoney(year.capital)}</td>
                    <td className="numeric">{formatMoney(year.interest)}</td>
                    <td className="numeric">{formatMoney(year.fees)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <div className="row between">
        <h2>Piano di ammortamento</h2>
        {canWrite && (
          <label className="button small">
            {schedule.isPending
              ? 'Caricamento…'
              : data.schedule.length
                ? 'Sostituisci il piano'
                : 'Carica il piano (Excel o CSV)'}
            <input
              type="file"
              accept=".xlsx,.csv"
              hidden
              onChange={(event) => void choose(event)}
            />
          </label>
        )}
      </div>
      {schedule.error && <p className="error">{schedule.error.message}</p>}
      {data.schedule.length === 0 ? (
        <p className="muted small">
          Nessun piano: le rate future si stimano ripetendo l’ultima. Il file ha una riga per rata
          con le colonne rata, scadenza, totale, interessi, capitale, spese, residuo.
        </p>
      ) : (
        <div className="table-wrap">
          <table className="data-table compact">
            <thead>
              <tr>
                <th>Rata</th>
                <th>Scadenza</th>
                <th className="numeric">Totale</th>
                <th className="numeric">Interessi</th>
                <th className="numeric">Capitale</th>
                <th className="numeric">Spese</th>
                <th className="numeric">Residuo</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.schedule.map((line) => (
                <tr key={line.instalment}>
                  <td>{line.instalment}</td>
                  <td>{formatDate(line.dueDate)}</td>
                  <td className="numeric">{formatMoney(line.total)}</td>
                  <td className="numeric">{formatMoney(line.interest)}</td>
                  <td className="numeric">{formatMoney(line.capital)}</td>
                  <td className="numeric">{formatMoney(line.fees)}</td>
                  <td className="numeric">{formatMoney(line.residual)}</td>
                  <td>{line.paid && <span className="pill ok">pagata</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data.notes && <p className="muted">{data.notes}</p>}
      {canWrite && (
        <p>
          <button type="button" className="link small" onClick={() => setRemoving(true)}>
            Elimina questo finanziamento
          </button>
        </p>
      )}
      {editing && <LoanDialog loan={data} onClose={() => setEditing(false)} />}
      {removing && (
        <ConfirmDialog
          title="Elimina il finanziamento"
          confirmLabel="Elimina"
          busy={remove.isPending}
          error={remove.error?.message ?? null}
          onConfirm={() => remove.mutate()}
          onCancel={() => setRemoving(false)}
        >
          <p>
            Le rate riconosciute dal numero tornano senza mutuo; i movimenti abbinati a mano tornano
            da riconciliare.
          </p>
        </ConfirmDialog>
      )}
    </section>
  );
}
